﻿import state from './../state';
import cycle from './../cycle';
import {LifeCycle} from '../cycle';
import {parseCSSColor, stringify, getHash, cloneObject} from './../functions';
import BlueprintProperties from './properties';

class DefaultRenderer {

    constructor() {

        this.canvasElement = null;
        this.renderOutput = {result: false};
        this.rootController = null;
        this.cycleCallback = [this, this.cycle];
        this.delta = 0;
        this.que = new Map();
        this.runningQue = new Map();
        this.forceGlobalUpdate = false;

    }

    setCanvas(element){
        this.canvasElement = element;
    }

    start(blueprint, watchGlobalUpdate=true){

        this.watchGlobalUpdate = watchGlobalUpdate;

        // if type is a string we already have the instance
        if(typeof blueprint.type == 'string'){
            this.rootController = blueprint;
        }else{
            // we have a blueprint, create the instance
            let Ctrl = blueprint.type;
            this.rootController = new Ctrl(this, blueprint);
        }
        this.rootController.render(this);

        cycle.addListener(this.cycleCallback);
    }

    stop(){
        cycle.removeListener(this.cycleCallback);
    }

    cycle(delta){
        if(this.runningQue.size > 0){
            console.warn('still running previous cycle, skipping this cycle');
            return; // lets finish the que first
        }
        this.delta = delta;
        let size = this.que.size;
        this.runningQue = new Map(this.que);
        this.que.clear();
        if(state.forceGlobalUpdate && this.watchGlobalUpdate){
            state.forceGlobalUpdate = false;
            this.forceGlobalUpdate = true;
            this.rootController.render(this);
        }else{
            let keys = [];
            for(let uid of this.runningQue.keys()){
                keys.push(uid);
            }
            for(let uid of keys){
                let ctrl = this.runningQue.get(uid);
                if(ctrl){
                    ctrl.render(this);
                }
            }
        }
        this.runningQue.clear();
        this.forceGlobalUpdate = false;
    }

    render(ctrl, blueprint) {

        // this renders the objects if they are changed or if the renderer
        // redraws each cycle.
        // todo: create an effective way of setting the state for the renderer
        // (Canvas, WebGL) for each object (style, transform, etc) so that a
        // minimum of state change are required
        if(! ctrl.blueprint){
            return;
        }

        let previous = Object.assign({ children: [], calcProps: new Map() }, ctrl.renderOutput);

        let current = { calcProps: new Map() };
        ctrl.newPropertyValues = false;

        if (! ctrl.renderOutput) {
            // There is no memory of this node, create and (when needed) append to parent
            this.createNode(ctrl, blueprint);
            ctrl.newPropertyValues = true;
            // this.setEvents(ctrl, blueprint);
        }
        if(ctrl.renderOutput){
            current.result = ctrl.renderOutput.result || null;
        }

        if(blueprint.properties && blueprint.properties.data){
            this.setCalculatedData(blueprint.properties.data, ctrl);
        }

        current.calcProps = this.getCalculatedProperties(blueprint.properties,
                ctrl, previous.calcProps);

        if(! ctrl.initialized){
            ctrl.init(this);
        }

        if(ctrl.hasTransformation()){
            this.setTransform(ctrl, ctrl.getTransformation());
        }

        this.renderNode(ctrl, blueprint, current.calcProps);

        // ctrl.updateChildren can also be null, then we should render the children
        // todo: perhaps we can replace updateChildren with combination of 
        // newPropertyValues and hasVariableProperties?
        if(this.forceGlobalUpdate || ctrl.updateChildren !== false){
            current.children = this.children(ctrl, blueprint, previous);
        }else{
            current.children = previous.children;
        }

        return current;

    }

    setCalculatedData(properties, ctrl, entryPoint=null){
        if(entryPoint === null){
            ctrl.data = {};
            entryPoint = ctrl.data;
        }
        if(properties && typeof properties == 'object'){
            for (let key of Object.keys(properties)) {
                let value = properties[key];
                if (value instanceof Function) {
                    entryPoint[key] =  value(ctrl);
                    ctrl.hasVariableProperties = true;
                } else if (value && value.constructor && value.constructor === Object) {
                    // recurrent call
                    if(! entryPoint[key]){
                        entryPoint[key] = {};
                    }
                    this.setCalculatedData(value, ctrl, entryPoint[key]);
                } else{
                    entryPoint[key] = value;
                }
            }
        }
    }

    getCalculatedProperties(properties, ctrl, previous, path=[]) {
        let current = new Map();
        let removeProps = new Map(previous);

        if(! properties && typeof properties != 'object'){
            return current;
        }
        for (let key of Object.keys(properties)) {
            if(key == 'data' && path.length == 0){
                continue;
            }
            let fullKey = [...path, key].join('.');
            let value = properties[key];
            if (value instanceof Function) {
                current.set(fullKey, value(ctrl));
                ctrl.hasVariableProperties = true;
            } else if (value && value.constructor && value.constructor === Object) {
                // recurrent call
                let prev = (previous)? previous.get(key): null;
                current.set(fullKey, this.getCalculatedProperties(value,
                         ctrl, prev, [...path, key]));
                if(ctrl.renderOutput && removeProps.has(fullKey)){
                    removeProps.delete(fullKey);
                }
                continue;
            } else {
                current.set(fullKey, value);
            }

            // compare with previous (only for DOM renderer)
            if (ctrl.renderOutput) {
                // compare with previous. Also if value has a transition, set the property again, the transition 
                // might not have been completed (the current property in DOM might not be equal to the end value)
                // todo: can we make this transition check more efficient?
                if ( ! previous || ! previous.has(fullKey) || previous.get(fullKey) !== current.get(fullKey) || 
                        (typeof previous.get(fullKey) == 'string' && previous.get(fullKey).indexOf('=>')) ){
                     // update property in renderOutput
                    let prev = (previous)? previous.get(fullKey): false;
                    this.setProperty(ctrl, this.getPropsExternalPath([...path, key]), current.get(fullKey), prev);
                }
                if(removeProps.has(fullKey)){
                    removeProps.delete(fullKey);
                }
            }
        }

        if (ctrl.renderOutput) {
            for(let [key, value] of removeProps){
                this.setProperty(ctrl, this.getPropsExternalPath(key.split('.')), null, value);
            }
        }

        return current;
    }

    getPropsExternalPath(path){

        let props = BlueprintProperties;
        let newPath = [];
        for(let p of path){
            if(props && props[p]){
                newPath.push(props[p].name);
                props = props[p].children || false;
            }else{
                newPath.push(p);
            }
        }
        return newPath;
    }

    createNode(ctrl, blueprint){
        // overwrite this method in the renderers implementation
        ctrl.setRenderOutput(true);
    }

    attach(){
        // overwrite this method in the renderers implementation
    }
    

    getEventBindingElement(ctrl){
        return false;
    }

    /**
     * If we want to set a transition to a property:
     * 200px => transition 500 cubic
     */
    setProperty(ctrl, keys, value, previous){
        ctrl.newPropertyValues = true;
        value = this.unserializePropertyValue(value);
        previous = (! previous)? [false]: this.unserializePropertyValue(previous);

        let result = value[0];

        if(value.length > 1){
            value[1] = value[1].split(' ');
            if(value[1][0] == 'transition'){
                let duration = (value[1].length > 1)? parseInt(value[1][1], 10): 500;
                let easing = (value[1].length > 2)? value[1][2]: 'linear';
                let init = previous[0];
                let p = this.getProperty(ctrl, keys);
                if(p){
                    init = p;
                }                
                if(init == false){
                    init = (value[1].length > 3)? value[1][3]: '0px';
                }
                result = this.setTransition(ctrl, keys, value[0], init, duration, easing);
            }
        }

        return result;
    }

    setTransition(ctrl, keys, value, previous, duration, easing){
        let v1 = (! previous)? false: this.getTransitionValue(previous);
        let v2 = this.getTransitionValue(value);
        // check if we have values for both of the same type and with the same unit
        if(v1 && v2 && v1[0] == v2[0] && v1[2] == v2[2]){
            let c = new LifeCycle([this, this.transitionCallback, ctrl, keys, v1, v2], 
                    duration, easing);
            ctrl.getQue().add(c, true, 'transitions', keys.join('.'));
            return previous;
        }
        return value;
    }

    setTransform(ctrl, transformation){

    }

    getTransitionValue(value){
        // remove the minus symbol, todo: do this in regular expr
        let fact = 1;
        if(value.substring(0,1) == '-'){
            fact = -1;
            value = value.substring(1);
        }
        
        let match = value.toString().match(/^([\d]*\.?[\d]+)([^\d]*)$/);
        if(match) {
            match[1] = (match[1].indexOf('.') >= 0)?
                    fact*parseFloat(match[1]): fact*parseInt(match[1], 10);
            match[0] = 'numeric';
            match[2] = (match.length > 2)? match[2]: ''; 
        }else{
            // try color
            let rgba = parseCSSColor(value);
            if(rgba){
                match = ['rgba', rgba, 'rgba']; 
            }
        }
        return match;
    }

    transitionCallback(fact, ctrl, keys, vFrom, vTo){
        let val = '';
        if(vTo[0] == 'rgba'){
            val = 'rgba(' + 
                (vFrom[1][0] + Math.round(fact * (vTo[1][0] - vFrom[1][0]))) + ',' +
                (vFrom[1][1] + Math.round(fact * (vTo[1][1] - vFrom[1][1]))) + ',' +
                (vFrom[1][2] + Math.round(fact * (vTo[1][2] - vFrom[1][2]))) + ',' +
                (vFrom[1][3] + Math.round(fact * (vTo[1][3] - vFrom[1][3]))) + ')';
        }else{ // default numeric
            val = (vFrom[1] + fact * (vTo[1] - vFrom[1])) + vTo[2];
        }
        this.setProperty(ctrl, keys, val, null);
    }
        

    unserializePropertyValue(input){
        let output = [];
        if(typeof input != 'undefined' && input != null){
            output = input.toString().split('=>')
            for(let key in output){
                output[key] = output[key].trim();
            }         
        }
        return output;
    }

    renderNode(ctrl, blueprint, properties){
        // overwrite this method in the renderers implementation
    }

    detach(ctrl){
        
        this.runningQue.delete(ctrl.uid);
        this.que.delete(ctrl.uid);
        
        // overwrite this method in the renderers implementation

    }

    children(ctrl, blueprint, previous) {

        let children = [];

        if (blueprint.children) {
            children = blueprint.children.slice(0);
            for(let key = 0; key < children.length; key++) {
                
                if (children[key] instanceof Function) {

                    let result = children[key](ctrl);
                    if(result instanceof Array){
                        children.splice(key, 1, ...result);
                        // if the result is an empty array, skip it
                        if(result.length == 0){
                            key--;
                        }
                    }else{
                        children.splice(key, 1, result);
                    }

                }
            }
        }


        let newChildren = [];
        let notFoundIds = [];
        for(let i = 0; i < children.length; i++) {
            
            let child = children[i];
            // ignore empty children
            if(! child || child instanceof Array){ continue; }    

            this.setChildHashKey(child);
            let found = false;

            
            // if forceGlobalUpdate is set to true, do not consider the previous child,
            // just replace everything. Todo: previously tried to keep unchanged children,
            // but that fails when their own childHash didn't change, but the children of
            // the node are different.
            if(! this.forceGlobalUpdate && previous.children && previous.children.length > 0){

                for(let k = 0; k < previous.children.length; k++) {
                    let prevChild = previous.children[k];
                    
                    // we found an old child with the same values, just
                    // use the old version without updating it
                    if (prevChild.keyLength == child.keyLength && 
                            prevChild.key == child.key) {

                        let c = previous.children.splice(k, 1)[0];
                        newChildren.push(c);
                        //only rerender the node when force update is set
                        // if(state.forceGlobalUpdate){
                        //      newChildren[newChildren.length-1].instance.render(this);
                        // }
                        
                        if(ctrl.newPropertyValues == true && c.instance.hasVariableProperties){
                            c.instance.render.call(c.instance, this);
                        }
                        found = true;
                        break;

                    // id is the same, but data different. Update this node
                    // with the new data.
                    }else if(child.uid && prevChild.uid && 
                            child.uid == prevChild.uid){
                        let nc = previous.children.splice(k, 1)[0];
                        // reset will also trigger a rerender
                        child.instance = nc.instance;

                        child.instance.reset.call(child.instance, child, this);
                        newChildren.push(child);

                        found = true;
                        break;

                    }
                                        
                }
            }

            // we couldn't find an old node to replace, keep child in memory
            if (! found) {
                notFoundIds.push(i);                
            }

        }

        let newChildrenToAdd = [];
        for(let i of notFoundIds){
            let child = children[i];
            let found = false; 
            if(previous.children && previous.children.length > 0){
                for(let k = 0; k < previous.children.length; k++) {
                    let prevChild = previous.children[k];

                    // we found a previous node of the same type, use this
                    // (more efficient than creating a new one, I think)
                    // Class.name is empty in IE:
                    // I believe this doensn't work in old Firefox and IE
                    if(prevChild.type.name && child.type.name && prevChild.type.name != '' && 
                            prevChild.type.name === child.type.name){
                        let nc = previous.children.splice(k, 1)[0];
                        // reset will also trigger a rerender
                        child.instance = nc.instance;
                        child.instance.reset.call(child.instance, child, this);
                        newChildren.splice(i, 0, child);

                        found = true;
                        break;
                    }
                }
            }
            if(! found){         
                // we add the blueprint at the right location.
                // we do not initialize them here, first remove the previous children
                // this prevents cases of overwriting children with same uid.
                newChildren.splice(i, 0, child);
                newChildrenToAdd.push(child);
                
            }
        }


        // remove all previous nodes that are no longer in use
        if(previous.children && previous.children.length > 0){
            while(previous.children.length > 0){
                let child = previous.children.splice(0,1)[0];
                child.instance.remove(this, false);
            }
        }
       
        for(let child of newChildrenToAdd){
            // create a new node
            child.instance = new child.type(ctrl, child);
            child.instance.render.call(child.instance, this);
        }

        ctrl.children = [];
        for(let i = 0; i < newChildren.length; i++){
            // set all the new child instances in the controller
            ctrl.children.push(newChildren[i].instance);

            // check order in DOM
            if(ctrl.renderOutput && ctrl.renderOutput.result && ctrl.renderOutput.result.hasChildNodes()){
                if(newChildren[i].instance.renderOutput && ctrl.renderOutput.result.childNodes[i] != newChildren[i].instance.renderOutput.result){
                    ctrl.renderOutput.result.insertBefore(
                            newChildren[i].instance.renderOutput.result, 
                            ctrl.renderOutput.result.childNodes[i]);
                }
            }
        }

        return newChildren;

    }

    setChildHashKey(child){
        let props = stringify(child.properties);
        child.keyLength = props.length;
        if(! child.type){
            console.log(child);
        }
        if(child.uid){
            props += child.uid;
        }
        if(child.children){
            props += child.children.length;
        }
        child.key = getHash(child.type.name + props);
    }

    remove(removeNodes=true){
        this.stop();
        if(removeNodes && this.rootController){
            this.rootController.remove(this, true);
        }
        delete this.canvasElement;
        delete this.renderOutput;
        delete this.rootController;
        delete this.cycleCallback;
    }
    

}

export default DefaultRenderer;